import { useCallback, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import usePublisher from 'api/usePublisher';
import { useUserDetails } from 'api/useUserDetails';
import { EEventsType } from 'constants/enums';
import {
  BigQueryEventData,
  useBigQueryEventsTracking
} from 'hooks/useBigQueryTracking';
import { trackMixpanelEvent } from 'utils/mixpanelClient';

type AnalyticsEventProperties = Record<string, unknown>;

interface IUseDashboardAnalyticsTrackingParams {
  tab: string;
  page: string;
  bigQueryDefaults?: Partial<BigQueryEventData>;
  mixpanelDefaults?: AnalyticsEventProperties;
}

interface ITrackEventProperties {
  bigQueryProperties?: Partial<BigQueryEventData>;
  mixpanelProperties?: AnalyticsEventProperties;
}

export interface IDashboardAnalyticsTracking {
  trackEvent: (
    eventName: EEventsType,
    properties?: ITrackEventProperties
  ) => void;
  isPublisherDataReady: boolean;
}

export const useDashboardAnalyticsTracking = ({
  tab,
  page,
  bigQueryDefaults,
  mixpanelDefaults
}: IUseDashboardAnalyticsTrackingParams): IDashboardAnalyticsTracking => {
  const { publisherId } = useParams<{ publisherId: string }>();
  const { userDetails } = useUserDetails();
  const { getPublisher } = usePublisher(publisherId || undefined);
  const { trackBigQueryEvent } = useBigQueryEventsTracking();

  const publisherData = getPublisher?.data;
  const isPublisherDataReady =
    !!getPublisher?.isFetched || getPublisher?.fetchStatus === 'idle';

  const bigQueryBase: Partial<BigQueryEventData> = useMemo(
    () => ({
      publisherId: publisherId || '',
      tab,
      page,
      role: userDetails?.role,
      businessName: publisherData?.companyName,
      solution: publisherData?.publisherSolutionModel,
      solutionSubtype: publisherData?.solutionModelSubtype,
      ...bigQueryDefaults
    }),
    [
      publisherId,
      tab,
      page,
      userDetails?.role,
      publisherData?.companyName,
      publisherData?.publisherSolutionModel,
      publisherData?.solutionModelSubtype,
      bigQueryDefaults
    ]
  );

  const mixpanelBase: AnalyticsEventProperties = useMemo(
    () => ({
      publisher_id: publisherId || '',
      user_id: userDetails?.userId,
      user_email: userDetails?.email,
      tab,
      page,
      user_role: userDetails?.role,
      business_name: publisherData?.companyName,
      solution: publisherData?.publisherSolutionModel,
      solution_subtype: publisherData?.solutionModelSubtype,
      ...mixpanelDefaults
    }),
    [
      publisherId,
      tab,
      page,
      userDetails?.userId,
      userDetails?.email,
      userDetails?.role,
      publisherData?.companyName,
      publisherData?.publisherSolutionModel,
      publisherData?.solutionModelSubtype,
      mixpanelDefaults
    ]
  );

  const trackEvent = useCallback(
    (eventName: EEventsType, properties?: ITrackEventProperties) => {
      trackBigQueryEvent({
        eventName,
        ...bigQueryBase,
        ...properties?.bigQueryProperties
      });
      trackMixpanelEvent(eventName, {
        ...mixpanelBase,
        ...properties?.mixpanelProperties
      });
    },
    [bigQueryBase, mixpanelBase, trackBigQueryEvent]
  );

  return { trackEvent, isPublisherDataReady };
};
